import { useColorMode, useEventListener } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { HomeView, Language } from '@/enums/common'
import { setDayjsLocale } from '@/libs/dayjs'
import i18n from '@/libs/i18n'
import type { LoggedInUserData } from '@/types/Auth'

export const useAppStore = defineStore('app', () => {
    // Startbox authenticates with an HttpOnly cookie set by the plane, so the SPA never
    // sees a raw token. The ref is kept for the house fetch shape: when it is empty no
    // Authorization header is sent and the cookie carries the session.
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const locale       = ref(localStorage.getItem('locale') || Language.English)
    const homeView     = ref(localStorage.getItem('home_view') || HomeView.Groups)
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<LoggedInUserData>()

    // Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark(): the plugin
    // persists the preference under `vueuse-color-scheme`, and every useColorMode() built on
    // that key syncs through the storage event, so this is one source of truth, not a second.
    //
    // NO `emitAuto`: the ref always reads the RESOLVED mode, 'light' or 'dark'. The house
    // theme has two modes and no System option (vue-spec theme.md), so a first visit — nothing
    // stored yet — simply opens in whatever the OS prefers, and the first click on the topbar
    // toggle persists an explicit choice from then on.
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => !!userData.value
    const isAdmin        = () => !!userData.value?.is_admin
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    // NetBird is optional: with it off the plane serves every port link through Traefik
    // instead of the mesh, so the UI drops every mesh-only affordance and hint.
    const isNetbirdEnabled = () => !!userData.value?.netbird_enabled

    const currentUser = computed(() => userData.value)

    // Keep the breakpoint LIVE: read once, `isMobile` would freeze the form factor (and the
    // shell's mobile drawer with it) until a reload. Bound to the store's effect scope.
    useEventListener(window, 'resize', () => { windowWidth.value = window.innerWidth })

    const changeLocale = (newLocale: string) => {
        const value = newLocale === Language.Slovenian ? Language.Slovenian : Language.English
        locale.value = value
        localStorage.setItem('locale', value)
        ;(i18n.global.locale as unknown as { value: string }).value = value
        setDayjsLocale(value)
    }

    const setHomeView = (view: string) => {
        homeView.value = view
        localStorage.setItem('home_view', view)
    }

    const setLogin = (user: LoggedInUserData) => {
        userData.value = user
        if (user.language) changeLocale(user.language)
        if (user.home_view) setHomeView(user.home_view)
    }

    const setLogout = () => {
        userData.value = undefined
        localStorage.removeItem('JWT')
        JWT.value = ''
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    changeLocale(locale.value)

    return {
        JWT, locale, homeView, userData, loaderActive, colorMode, currentUser,
        getJWT, isLoggedIn, isAdmin, isLoaderActive, isMobile, isNetbirdEnabled,
        setLogin, setLogout, setHomeView, startLoading, stopLoading, changeLocale,
    }
})
